
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useToast } from "@/components/ui/use-toast";
import { Card, CardContent } from "@/components/ui/card";

interface InquiryFormProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  service?: string | null;
  onClose?: () => void;
  formType?: "service" | "solution" | "job" | "contact";
}

export default function InquiryForm({ 
  service = '', 
  formType = "service" 
}: InquiryFormProps) {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    budget: "",
    deadline: "",
    description: "",
    resume: ""
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const successMessages = {
      service: `We'll get back to you soon about ${service}`,
      solution: `We'll reach out to you shortly about the ${service} solution`,
      job: `Your application for ${service} has been received`,
      contact: `Your message has been sent. We'll contact you soon.`
    };
    
    toast({
      title: "Submission Successful!",
      description: successMessages[formType],
    });
    
    setFormData({
      name: "",
      email: "",
      phone: "",
      budget: "",
      deadline: "",
      description: "",
      resume: ""
    });
  };

  const getFormTitle = () => {
    switch (formType) {
      case "service":
        return "Service Inquiry";
      case "solution":
        return "Solution Request";
      case "job":
        return "Job Application";
      case "contact":
        return "Contact Request";
      default:
        return "Inquiry";
    }
  };

  return (
    <ScrollArea className="h-[80vh] mt-4 pr-4">
      <Card className="mb-6 bg-indigo-50 dark:bg-indigo-900/20 border-0">
        <CardContent className="p-4">
          <div className="flex flex-col space-y-1">
            <h3 className="text-lg font-medium">{getFormTitle()}</h3>
            <p className="text-sm font-semibold text-indigo-600 dark:text-indigo-400">{service}</p>
          </div>
        </CardContent>
      </Card>
      
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="space-y-2">
          <label htmlFor="name" className="text-sm font-medium">
            Full Name *
          </label>
          <Input
            id="name"
            required
            placeholder="Enter your full name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          />
        </div>
        <div className="space-y-2">
          <label htmlFor="email" className="text-sm font-medium">
            Email Address *
          </label>
          <Input
            id="email"
            type="email"
            required
            placeholder="Enter your email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          />
        </div>
        <div className="space-y-2">
          <label htmlFor="phone" className="text-sm font-medium">
            Phone Number *
          </label>
          <Input
            id="phone"
            required
            placeholder="Enter your phone number"
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
          />
        </div>
        
        {formType !== "job" && formType !== "contact" && (
          <>
            <div className="space-y-2">
              <label htmlFor="budget" className="text-sm font-medium">
                Budget Range *
              </label>
              <Input
                id="budget"
                required
                placeholder="Enter your budget range"
                value={formData.budget}
                onChange={(e) => setFormData({ ...formData, budget: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="deadline" className="text-sm font-medium">
                Project Deadline *
              </label>
              <Input
                id="deadline"
                type="date"
                required
                value={formData.deadline}
                onChange={(e) => setFormData({ ...formData, deadline: e.target.value })}
              />
            </div>
          </>
        )}
        
        {formType === "job" && (
          <div className="space-y-2">
            <label htmlFor="resume" className="text-sm font-medium">
              Resume/CV Link *
            </label>
            <Input
              id="resume"
              required
              placeholder="Paste your resume link (Google Drive, Dropbox, etc.)"
              value={formData.resume}
              onChange={(e) => setFormData({ ...formData, resume: e.target.value })}
            />
          </div>
        )}
        
        <div className="space-y-2">
          <label htmlFor="description" className="text-sm font-medium">
            {formType === "job" ? "Cover Letter *" : 
             formType === "contact" ? "Message *" : "Project Description *"}
          </label>
          <Textarea
            id="description"
            required
            placeholder={formType === "job" 
              ? "Tell us why you're a good fit for this position" 
              : formType === "contact"
              ? "Please let us know how we can help you"
              : "Please describe your project requirements"}
            className="min-h-[150px]"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          />
        </div>
        <Button 
          type="submit"
          className="w-full curved-xl btn-gradient text-white hover:shadow-lg transition-all duration-300"
        >
          {formType === "job" ? "Submit Application" : 
           formType === "contact" ? "Send Message" : "Submit Inquiry"}
        </Button>
      </form>
    </ScrollArea>
  );
}
